import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CalendarPeriod,
  formatRangeForApi,
  getPeriodBounds,
} from "../calendarPeriod";
import { CalendarViewMode, DateRange } from "../types";

export const useTaskCalendarState = () => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<CalendarViewMode>("schedule");
  const [currentPeriod, setCurrentPeriod] =
    useState<CalendarPeriod>("dayGridMonth");
  const [anchorDate, setAnchorDate] = useState(() => new Date());
  const [range, setRange] = useState<DateRange>({ start: "", end: "" });

  const periodOptions = useMemo(
    () => [
      {
        value: "timeGridDay" as const,
        label: t("taskHub.schedule.day") || "Day",
      },
      {
        value: "timeGridWeek" as const,
        label: t("taskHub.schedule.week") || "Week",
      },
      {
        value: "dayGridMonth" as const,
        label: t("taskHub.schedule.month") || "Month",
      },
    ],
    [t]
  );

  const viewModeOptions = useMemo(
    () => [
      {
        label: t("taskHub.schedule.schedule") || "Schedule",
        value: "schedule" as const,
      },
      {
        label: t("taskHub.schedule.ganttView") || "Gantt View",
        value: "gantt" as const,
      },
    ],
    [t]
  );

  useEffect(() => {
    if (viewMode !== "gantt") {
      return;
    }

    const { start, end } = getPeriodBounds(currentPeriod, anchorDate);
    setRange(formatRangeForApi(start, end));
  }, [viewMode, currentPeriod, anchorDate]);

  const ganttPeriodBounds = useMemo(
    () => getPeriodBounds(currentPeriod, anchorDate),
    [currentPeriod, anchorDate]
  );

  const handleViewModeChange = (nextMode: CalendarViewMode) => {
    if (nextMode === "gantt" && range.start) {
      setAnchorDate(new Date(range.start));
    }
    setViewMode(nextMode);
  };

  const handleScheduleDatesChange = (
    period: CalendarPeriod,
    start: Date,
    end: Date
  ) => {
    setCurrentPeriod(period);
    setAnchorDate(start);
    setRange(formatRangeForApi(start, end));
  };

  return {
    t,
    viewMode,
    currentPeriod,
    anchorDate,
    range,
    periodOptions,
    viewModeOptions,
    ganttPeriodBounds,
    setAnchorDate,
    setCurrentPeriod,
    handleViewModeChange,
    handleScheduleDatesChange,
  };
};
